/**
 * PATH: apps/quote/src/components/builder/panels/ai-assistant/ai-assistant-types.ts
 * PURPOSE: Types and config constants for the AI Assistant panel
 */

import {
  Sparkles, ShieldCheck, TrendingUp, FileSearch,
  AlertTriangle, Plus, AlertCircle, Zap,
} from 'lucide-react'
import type { AISuggestion, QuoteDetail, QuoteAuditResult, SimilarQuotesResult, PricingOptimizationResult, CoverageCheckResult } from '@/lib/quoteApi'

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  suggestions?: AISuggestion[]
  auditResult?: QuoteAuditResult
  similarQuotes?: SimilarQuotesResult
  pricingResult?: PricingOptimizationResult
  coverageResult?: CoverageCheckResult
  timestamp: Date
}

export interface AIAssistantPanelProps {
  quoteId?: string
  quote?: QuoteDetail | null
  onApplySuggestion?: (suggestion: AISuggestion) => void
  onOpenQuote?: (quoteId: string) => void
}

export interface BackendAISuggestion {
  id: string
  suggestion_type: 'price_adjustment' | 'quantity_check' | 'missing_item' | 'duplicate_warning' | 'productivity_suggestion'
  original_value: Record<string, any>
  suggested_value: Record<string, any>
  confidence_score: number
  confidence_level: 'high' | 'medium' | 'low'
  reasoning: string
  status: 'pending' | 'accepted' | 'rejected' | 'modified'
  line_item_description?: string
}

export interface AISuggestionsResponse {
  suggestions: BackendAISuggestion[]
  batch_id: string
  total: number
  high_confidence: number
}

export const QUICK_ACTIONS = [
  { id: 'suggestions', icon: Sparkles, label: 'AI Suggest', description: 'Price & quantity checks', color: 'text-indigo-600', bgColor: 'bg-indigo-50 dark:bg-indigo-900/20' },
  { id: 'audit', icon: ShieldCheck, label: 'Audit', description: 'Check for issues', color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  { id: 'optimize', icon: TrendingUp, label: 'Optimize', description: 'Improve margins', color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  { id: 'similar', icon: FileSearch, label: 'Similar', description: 'Find reference quotes', color: 'text-purple-600', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
]

export const SUGGESTION_TYPE_CONFIG: Record<string, { icon: React.ComponentType<Record<string, unknown>>; color: string; bgColor: string; label: string }> = {
  price_adjustment: { icon: TrendingUp, color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-900/20', label: 'Price Adjustment' },
  quantity_check: { icon: AlertTriangle, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-900/20', label: 'Quantity Check' },
  missing_item: { icon: Plus, color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-900/20', label: 'Missing Item' },
  duplicate_warning: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-900/20', label: 'Duplicate Warning' },
  productivity_suggestion: { icon: Zap, color: 'text-purple-600', bgColor: 'bg-purple-50 dark:bg-purple-900/20', label: 'Productivity' },
}
